// ToneAnalyticsPage - E007-S018
// Dashboard for emotional tone distribution and analytics
import React, { useState, useEffect } from 'react';
import { toneApi, ToneAnalytics, ToneDistribution } from '../lib/toneApi';
import { EmotionalTone, TONE_CONFIG } from '../components/EmotionalToneFilter';
import { ToneBadge, getToneColor } from '../components/ToneBadge';
import { BatchClassifyProgress, useBatchClassify } from '../components/BatchClassifyProgress';

export const ToneAnalyticsPage: React.FC = () => {
  const [analytics, setAnalytics] = useState<ToneAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  
  const { jobId, isStarting, startBatch, reset } = useBatchClassify();

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const fromDate = getDateFrom(dateRange);
      const toDate = new Date().toISOString().split('T')[0];
      
      const data = await toneApi.getDistribution(
        dateRange === 'all' ? undefined : { from: fromDate, to: toDate }
      );
      setAnalytics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const getDateFrom = (range: string): string => {
    const now = new Date();
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
    now.setDate(now.getDate() - days);
    return now.toISOString().split('T')[0];
  };

  const handleClassifyAll = async () => {
    try {
      await startBatch();
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleClassifyComplete = () => {
    reset();
    loadAnalytics();
  };

  // Calculate chart data
  const maxCount = analytics?.distribution.reduce((max, d) => Math.max(max, d.count), 0) || 1;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-white">
            Emotional Tone Analytics
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Analyze the emotional tone distribution of your creatives
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Date Range Selector */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as typeof dateRange)}
            className="px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-white dark:bg-boxdark text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>

          {/* Classify All Button */}
          <button
            onClick={handleClassifyAll}
            disabled={isStarting || !!jobId}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isStarting ? (
              <>
                <span className="animate-spin">⏳</span>
                Starting...
              </>
            ) : (
              <>
                <span>🔄</span>
                Classify Untagged
              </>
            )}
          </button>
        </div>
      </div>

      {/* Batch Progress */}
      {jobId && (
        <BatchClassifyProgress
          jobId={jobId}
          onComplete={handleClassifyComplete}
        />
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Analytics Content */}
      {!loading && analytics && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Creatives</p>
              <p className="text-2xl font-bold text-black dark:text-white">
                {analytics.total_creatives}
              </p>
            </div>
            
            {/* Top Performing Tone */}
            {analytics.distribution.length > 0 && (
              <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Top Performer</p>
                <div className="mt-1">
                  <ToneBadge 
                    tone={[...analytics.distribution].sort((a, b) => b.avg_roas - a.avg_roas)[0].tone}
                    size="lg"
                  />
                </div>
              </div>
            )}

            {/* Most Common */}
            {analytics.distribution.length > 0 && (
              <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Most Common</p>
                <div className="mt-1">
                  <ToneBadge 
                    tone={[...analytics.distribution].sort((a, b) => b.count - a.count)[0].tone}
                    size="lg"
                  />
                </div>
              </div>
            )}

            {/* Avg ROAS */}
            <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Avg ROAS</p>
              <p className="text-2xl font-bold text-black dark:text-white">
                {(analytics.distribution.reduce((sum, d) => sum + d.avg_roas * d.count, 0) / 
                  analytics.distribution.reduce((sum, d) => sum + d.count, 0) || 0).toFixed(2)}x
              </p>
            </div>
          </div>

          {/* Distribution Chart */}
          <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4">
            <h3 className="text-lg font-semibold text-black dark:text-white mb-4">
              Tone Distribution
            </h3>
            
            <div className="space-y-4">
              {TONE_CONFIG.map(toneConfig => {
                const data = analytics.distribution.find(d => d.tone === toneConfig.name);
                const percent = data?.percent || 0;
                const count = data?.count || 0;
                
                return (
                  <div key={toneConfig.name} className="flex items-center gap-4">
                    <div className="w-24 flex items-center gap-2">
                      <span>{toneConfig.emoji}</span>
                      <span className={`capitalize font-medium ${toneConfig.color}`}>
                        {toneConfig.name}
                      </span>
                    </div>
                    
                    <div className="flex-1">
                      <div className="w-full bg-gray-200 dark:bg-meta-4 rounded-full h-6 relative">
                        <div
                          className="h-6 rounded-full transition-all duration-500"
                          style={{ 
                            width: `${percent}%`,
                            backgroundColor: getToneColor(toneConfig.name as EmotionalTone)
                          }}
                        />
                        <span className="absolute inset-0 flex items-center justify-center text-sm font-medium text-black dark:text-white">
                          {count} ({percent.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Performance by Tone */}
          <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4">
            <h3 className="text-lg font-semibold text-black dark:text-white mb-4">
              Performance by Tone
            </h3>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stroke dark:border-strokedark">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Tone</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Count</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Avg CTR</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Avg ROAS</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Performance</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.distribution
                    .sort((a, b) => b.avg_roas - a.avg_roas)
                    .map(dist => {
                      const config = TONE_CONFIG.find(t => t.name === dist.tone);
                      const performanceScore = (dist.avg_performance * 100);
                      
                      return (
                        <tr 
                          key={dist.tone} 
                          className="border-b border-stroke dark:border-strokedark hover:bg-gray-50 dark:hover:bg-meta-4"
                        >
                          <td className="py-3 px-4">
                            <ToneBadge tone={dist.tone} />
                          </td>
                          <td className="text-right py-3 px-4 text-black dark:text-white">
                            {dist.count}
                          </td>
                          <td className="text-right py-3 px-4 text-black dark:text-white">
                            {(dist.avg_ctr * 100).toFixed(2)}%
                          </td>
                          <td className="text-right py-3 px-4 text-black dark:text-white font-medium">
                            {dist.avg_roas.toFixed(2)}x
                          </td>
                          <td className="text-right py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-20 bg-gray-200 dark:bg-meta-4 rounded-full h-2">
                                <div
                                  className="h-2 rounded-full"
                                  style={{ 
                                    width: `${Math.min(performanceScore, 100)}%`,
                                    backgroundColor: performanceScore > 70 ? '#22c55e' : performanceScore > 40 ? '#eab308' : '#ef4444'
                                  }}
                                />
                              </div>
                              <span className="text-sm text-gray-500 w-12 text-right">
                                {performanceScore.toFixed(0)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

          {/* AI Insights */}
          {analytics.insights && analytics.insights.length > 0 && (
            <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-4 flex items-center gap-2">
                <span>💡</span>
                AI Insights
              </h3>
              
              <div className="space-y-3">
                {analytics.insights.map((insight, idx) => (
                  <div 
                    key={idx}
                    className="p-3 bg-gray-50 dark:bg-meta-4 rounded-lg text-sm text-gray-700 dark:text-gray-300"
                  >
                    {insight}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ToneAnalyticsPage;
